import { StrictMode, type ReactNode } from "react";
import { createRoot } from "react-dom/client";
import { MantineProvider } from "@mantine/core";
import { DatesProvider } from "@mantine/dates";
import { ModalsProvider } from "@mantine/modals";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
// dayjs ships English; every other locale is opt-in, and Mantine's calendars read their
// month and weekday names from it rather than from the i18next catalogue.
import "dayjs/locale/ru";
import { App } from "./App";
import { DevelopmentGate } from "./ui/DevelopmentGate";
import { useLanguage } from "./i18n/useLanguage";
import { hydratePlayer } from "./state/resume";
import { nativeRuntime } from "./platform/native";
import { mantineCssVariables, mantineTheme } from "./ui/mantineTheme";
// The only CSS in the bundle. Dates comes after core because Mantine's own docs require it,
// and there is no third sheet: every rule this app used to ship is now a prop, a theme value
// or a `style` object, and `tokens.test.ts` fails the build if a `.css` file reappears.
import "@mantine/core/styles.css";
import "@mantine/dates/styles.css";
import "./i18n";

const client = new QueryClient({
  defaultOptions: {
    queries: {
      // The archive is mostly immutable history. Re-fetching it because a window regained
      // focus is noise; the few things that do move (now playing, bounds) declare their own
      // refetch interval.
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 30_000,
    },
  },
});

const root = document.getElementById("root");
if (!root) throw new Error("#root is missing from index.html");

// Browser resume is ready before the first render; a registered native runtime supplies the
// authoritative snapshot instead.
if (nativeRuntime() === null) hydratePlayer();
else {
  window.addEventListener("jtarchive:session-invalidated", () => {
    client.removeQueries({ queryKey: ["development-gate"] });
    void client.invalidateQueries();
  });
}

/**
 * The date components' language, kept level with the UI's.
 *
 * A component because the language is a hook, and this is above `App` -- which the admin
 * tree reaches by an early return, so wrapping inside it would cover only half the app.
 */
function Dates({ children }: { children: ReactNode }) {
  return <DatesProvider settings={{ locale: useLanguage() }}>{children}</DatesProvider>;
}

createRoot(root).render(
  <StrictMode>
    {/* `forceColorScheme` is the documented scheme pin: it makes `defaultColorScheme` and any
        colour-scheme manager inert and disables `setColorScheme`. There is no light mode. */}
    <MantineProvider
      theme={mantineTheme}
      forceColorScheme="dark"
      cssVariablesResolver={mantineCssVariables}
    >
      <QueryClientProvider client={client}>
        <Dates>
          {/* Inside the query client, so anything a modal draws can still read the cache;
              inside `Dates`, so a modal that grows a calendar gets the same language. */}
          <ModalsProvider>
            <DevelopmentGate>
              <App />
            </DevelopmentGate>
          </ModalsProvider>
        </Dates>
      </QueryClientProvider>
    </MantineProvider>
  </StrictMode>,
);
